import { KebabHorizontalIcon, PencilIcon, PlusIcon, SyncIcon, TrashIcon } from "@primer/octicons-react";
import { ActionList, ActionMenu, Box, Checkbox, useConfirm } from "@primer/react";
import { useEffect, useMemo, useState } from "react";
import { financeHelperApi } from "../../../api/financeHelperApi";
import { hinhThucNopBomApi } from "../../../api/hinhThucNopBomApi";
import Button from "../../../components/ui/button";
import DataTable from "../../../components/ui/data-table";
import Text from "../../../components/ui/text";
import { useCommonContext } from "../../../contexts/common";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { IHocSinh } from "../../../models/response/crm-hocsinh/IHocsinh";
import { IHinhThucNopBom } from "../../../models/response/finance/IHinhThucNopBom";
import { ITinhBaoPhi, ITinhBaoPhiVm } from "../../../models/response/finance/ITinhBaoPhi";
import BaoPhiCreateLayout from "./BaoPhiCreateLayout";
interface Props {
    hocSinh: IHocSinh;
}
const BaoPhiLayout: React.FC<Props> = ({ hocSinh }) => {
    const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isShowEditModal, setIsShowEditModal] = useState<boolean>(false)
    const [tinhBaoPhi, setTinhBaoPhi] = useState<ITinhBaoPhiVm[]>([]);
    const [tinhBaoPhiEditing, setTinhBaoPhiEditing] = useState<ITinhBaoPhiVm>();
    const [hinhThucNopBoms, setHinhThucNopBoms] = useState<IHinhThucNopBom[]>([]);
    const { translate } = useCommonContext();
    const confirm = useConfirm();
    useEffect(() => {
        handleReloadData();
        handleReloadHinhThucHinhThucNopBom();
    }, [])
    const dataSource = useMemo(() => {
        return tinhBaoPhi;
    }, [tinhBaoPhi])
    const handleReloadHinhThucHinhThucNopBom = async () => {
        const res = await hinhThucNopBomApi.selectAll();
        if (res.is_success) {
            setHinhThucNopBoms(res.data);
        }
    };
    const handleReloadData = async () => {
        setIsLoading(true)
        const datasend = { ts_hocsinh_id: hocSinh.id, nam_hoc: nam_hoc }
        const res = await financeHelperApi.sf_tinhbaophi_selectbyhocsinh(datasend);
        setIsLoading(false)
        if (res.is_success) {
            setTinhBaoPhi(res.data.data);
        } else {
            setTinhBaoPhi([]);
        }
    };
    
    const handleDelete = async (id: number) => {
        if (await confirm({
            content: translate("Base.Label.DeleteConfirm.Content"),
            title: translate("Base.Label.Confirm"),
            cancelButtonContent: translate("Base.Label.Close"),
            confirmButtonContent: translate("Base.Label.Delete"),
            confirmButtonType: "danger"
        })) {
            const res = await financeHelperApi.sf_tinhbaophi_delete({ sf_tinhbaophi_id: id, dm_coso_id: dm_coso_id });
            if (res.is_success) {
                handleReloadData();
            }

        }
    }
    return (
        <Box sx={{
            p: 1
        }}>
            {!isShowEditModal &&
                <DataTable
                    data={dataSource}
                    isLoading={isLoading}
                    title={translate("Lịch sử báo phí")}
                    searchEnable
                    paging={{
            enable: true,
            pageSizeItems: [20, 50, 200, 500, 1000],
        }}
                    actionComponent={
                        <Box sx={{
                            display: "flex"
                        }}>
                            <Button text="Tạo báo phí" leadingVisual={PlusIcon}
                                size="medium"
                                variant="primary"
                                onClick={() => {
                                    setTinhBaoPhiEditing(undefined)
                                    setIsShowEditModal(true);
                                }}
                            />
                            <Button text="Base.Label.Refresh" leadingVisual={SyncIcon}
                                size="medium"
                                sx={{ ml: 1 }}
                                onClick={() => {
                                    handleReloadData();
                                }}
                            />
                        </Box>
                    }
                    columns={[
                        {
                            id: "cmd",
                            caption: "#",
                            width: "50px",
                            align: "center",
                            cellRender: (data: ITinhBaoPhiVm) => {
                                return (
                                    <ActionMenu>
                                        <ActionMenu.Button icon={KebabHorizontalIcon} variant="invisible" sx={{ m: -1 }}>&nbsp;</ActionMenu.Button>
                                        <ActionMenu.Overlay width="auto">
                                            <ActionList>
                                                <ActionList.Item onSelect={() => {
                                                    setTinhBaoPhiEditing(data)
                                                    setIsShowEditModal(true)
                                                }}>
                                                    <ActionList.LeadingVisual><PencilIcon /></ActionList.LeadingVisual>
                                                    <Text text='Base.Label.Edit' />
                                                </ActionList.Item>
                                                <ActionList.Item variant="danger" onSelect={() => {
                                                    handleDelete(data.id)
                                                }}>
                                                    <ActionList.LeadingVisual><TrashIcon /></ActionList.LeadingVisual>
                                                    <Text text='Base.Label.Delete' />
                                                </ActionList.Item>
                                            </ActionList>
                                        </ActionMenu.Overlay>
                                    </ActionMenu>
                                );
                            }
                        },
                        {
                            dataField: "ngay_tao",
                            caption: translate("Ngày tạo"),
                            width: 120,
                            cellRender: (data: { ngay_tao: string }) => {
                                const formatDate = (isoDate: string) => {
                                    const date = new Date(isoDate);
                                    return date.toLocaleDateString("vi-VN", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                    });
                                };

                                return (
                                    <span>
                                        {data.ngay_tao ? formatDate(data.ngay_tao) : ""}
                                    </span>
                                );
                            },
                        },
                        {
                            dataField: "nguoi_tao",
                            caption: translate("Người tạo"),
                        },
                        {
                            dataField: "sf_hinhthucnopbom_ids",
                            caption: translate("Hình thức nộp"),
                            cellRender: (data: ITinhBaoPhi) => {
                                const ids = data.sf_hinhthucnopbom_ids.split(",").map(Number);
                                const names = ids
                                    .map(id => hinhThucNopBoms.find(bom => bom.id === id)?.name)
                                    .filter(Boolean);
                                const joinedNames = names.length > 0 ? names.join(", ") : "";

                                return (
                                    <Box>
                                        <span>{joinedNames}</span>
                                    </Box>
                                );
                            }
                        },
                        {
                            dataField: "pdf_file",
                            caption: translate("PDF file"),
                            cellRender: (data: { pdf_file: string }) => {
                                return (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        {data.pdf_file && (
                                            <a
                                                href={data.pdf_file}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    textDecoration: "none",
                                                    color: "inherit",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        color: "blue",
                                                        fontSize: "20px",
                                                        cursor: "pointer",
                                                    }}
                                                    title="Open PDF"
                                                >
                                                    📄
                                                </span>
                                            </a>
                                        )}
                                    </Box>
                                );
                            }
                        },
                        {
                            dataField: "receiver_email",
                            minWidth: 200,
                            caption: translate("Người nhận"),
                        },
                        {
                            dataField: "is_sended",
                            width: 120,
                            caption: translate("Đã gửi PH"),
                            cellRender: (data: ITinhBaoPhi) => {
                                return (
                                    <Box sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                    }}>
                                        <Checkbox checked={data.is_sended} />
                                    </Box>
                                )
                            }
                        },
                        {
                            dataField: "send_time",
                            width: 150,
                            caption: translate("Ngày gửi"),
                            cellRender: (data: { send_time: string }) => {
                                const formatDateTime = (isoDate: string) => {
                                    const date = new Date(isoDate);
                                    const formattedDate = date.toLocaleDateString("vi-VN", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                    });
                                    const formattedTime = date.toLocaleTimeString("vi-VN", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                        hour12: false, // 24-hour format
                                    });
                                    return `${formattedDate} ${formattedTime}`;
                                };

                                return (
                                    <span>
                                        {data.send_time ? formatDateTime(data.send_time) : ""}
                                    </span>
                                );
                            },
                        },
                        {
                            dataField: "send_user",
                            width: 200,
                            caption: translate("Người gửi"),
                        },
                    ]}
                />
            }
            {isShowEditModal &&
                <BaoPhiCreateLayout tinhBaoPhiEditing={tinhBaoPhiEditing} onCancel={() => {
                    setIsShowEditModal(false)
                }} hocSinh={hocSinh} />
            }
        </Box>
    );
};

export default BaoPhiLayout;